import { useMemo, type FC } from "react";
import styled from "styled-components";

import { MultipleValuesLabel, UnassignedLabel } from "@ds/components/forms/labels/common";
import { InfoSection } from "@ds/components/info-view";
import { isMultipleFieldValuesInAggregatedEntity } from "@ds/utils/entities";

import {
  contactFieldValueOrPlaceholder,
  getContactPhoneDisplayValue,
  getContactsArrayFromLocation,
  mapLocationContactsForForm,
} from "../../utils/contacts-helpers";

const ContactInfo = styled.address`
  color: var(--textColor080);
  display: grid;
  font-size: 0.9rem;
  font-style: normal;
  gap: 4px;
  grid-template-columns: auto 1fr;
  line-height: 1.2;
  margin-block-start: 4px;
`;

const ContactValue = styled.div`
  overflow: hidden;
  word-break: break-all;
`;

export const LocationContactsInfoSection: FC<{ location: Locality }> = ({ location }) => {
  const infoBody = useMemo(() => {
    if (isMultipleFieldValuesInAggregatedEntity(location, "contacts")) {
      return <MultipleValuesLabel />;
    }

    const contacts = mapLocationContactsForForm(getContactsArrayFromLocation(location) ?? []);

    if (!contacts.length) {
      return <UnassignedLabel text="Location(s) has no contacts" />;
    }

    return contacts.map((contact, index) => (
      <div key={index} className="align-items-start flex flex-column flex-wrap mb-3">
        <ContactValue as="b">{contactFieldValueOrPlaceholder(contact.name)}</ContactValue>
        <ContactInfo>
          <div>Email:</div>
          <ContactValue>{contactFieldValueOrPlaceholder(contact.email)}</ContactValue>
          <div>Phone:</div>
          <ContactValue>{contactFieldValueOrPlaceholder(getContactPhoneDisplayValue(contact.phone))}</ContactValue>
          <div>Title:</div>
          <ContactValue>{contactFieldValueOrPlaceholder(contact.title)}</ContactValue>
        </ContactInfo>
      </div>
    ));
  }, [location]);

  return (
    <InfoSection title="Contacts" icon="pi pi-user">
      {infoBody}
    </InfoSection>
  );
};
